import { useEffect, useRef } from "react";
import { focusNames } from "@/engine/labels";
import { advance, stepStart } from "@/engine/playback";
import { Viewer } from "@/engine/Viewer";
import { useViewerStore } from "@/state/store";

const isSmallScreen = () => window.matchMedia("(max-width: 820px)").matches;

/**
 * Owns the three.js engine for the lifetime of the canvas and keeps it in sync with the store:
 * model loading, step changes (frame + framing + highlight), playback loop, hover highlight, recentre.
 */
export function useViewer(
	canvasRef: React.RefObject<HTMLCanvasElement | null>,
	containerRef: React.RefObject<HTMLElement | null>,
) {
	const viewerRef = useRef<Viewer | null>(null);

	// engine lifecycle + render loop
	useEffect(() => {
		const canvas = canvasRef.current;
		const container = containerRef.current;
		if (!canvas || !container) return;
		const viewer = new Viewer(canvas, container, { lite: isSmallScreen() });
		viewerRef.current = viewer;
		const store = useViewerStore;
		const ro = new ResizeObserver(() => viewer.resize());
		ro.observe(container);

		let raf = 0;
		let last = performance.now();
		const loop = (now: number) => {
			raf = requestAnimationFrame(loop);
			const dt = Math.min((now - last) / 1000, 0.1);
			last = now;
			const s = store.getState();
			const step = s.data?.steps[s.stepIndex];
			if (step && s.data && s.playing) {
				const r = advance(step, s.frame, dt, s.data.product.fps, s.speed);
				viewer.setFrame(r.frame);
				if (r.done) {
					store.setState({ frame: r.frame, playing: false });
					store.getState().markSeen(step.code);
					viewer.highlight(focusNames(step), false);
				} else store.setState({ frame: r.frame });
			}
			viewer.tick(dt, !!step && s.frame < step.f1);
		};
		raf = requestAnimationFrame(loop);
		return () => {
			cancelAnimationFrame(raf);
			ro.disconnect();
			viewer.dispose();
			viewerRef.current = null;
		};
	}, [canvasRef, containerRef]);

	// load the model when the product data changes (`setData` sets the data and the model URL
	// together: a re-signed URL for the same files never reaches the store)
	const data = useViewerStore((s) => s.data);
	const modelUrl = useViewerStore((s) => s.modelUrl);
	useEffect(() => {
		const viewer = viewerRef.current;
		if (!viewer || !data || !modelUrl) return;
		let cancelled = false;
		viewer
			.load(modelUrl, data, (p) => useViewerStore.getState().setProgress(p))
			.then(() => {
				if (cancelled) return;
				const first = data.steps[0];
				if (!first) return;
				viewer.setFrame(stepStart(first));
				viewer.frameStep(first, true);
				viewer.highlight(focusNames(first), true);
				useViewerStore.setState({ status: "ready" });
			})
			.catch((err: unknown) => {
				if (!cancelled)
					useViewerStore
						.getState()
						.setError(err instanceof Error ? err.message : String(err));
			});
		return () => {
			cancelled = true;
		};
	}, [data, modelUrl]);

	// step change: frame, framing, highlight
	const stepIndex = useViewerStore((s) => s.stepIndex);
	const status = useViewerStore((s) => s.status);
	useEffect(() => {
		const viewer = viewerRef.current;
		const step = data?.steps[stepIndex];
		if (!viewer || !step || status !== "ready") return;
		viewer.clearHighlights();
		viewer.setFrame(stepStart(step));
		viewer.frameStep(step, false);
		viewer.highlight(focusNames(step), true);
	}, [data, stepIndex, status]);

	// scrubbing / replay set the frame from the UI
	const frame = useViewerStore((s) => s.frame);
	const playing = useViewerStore((s) => s.playing);
	useEffect(() => {
		if (!playing) viewerRef.current?.setFrame(frame);
	}, [frame, playing]);

	// replay: re-arm the highlight when playback restarts from the beginning
	useEffect(() => {
		const viewer = viewerRef.current;
		const step = data?.steps[stepIndex];
		if (viewer && step && playing) viewer.highlight(focusNames(step), true);
	}, [playing, data, stepIndex]);

	// hover from the parts chips
	const hovered = useViewerStore((s) => s.hovered);
	const prevHovered = useRef<string[]>([]);
	useEffect(() => {
		const viewer = viewerRef.current;
		if (!viewer) return;
		if (prevHovered.current.length)
			viewer.highlight(prevHovered.current, false, true);
		if (hovered.length) viewer.highlight(hovered, true, true);
		prevHovered.current = hovered;
	}, [hovered]);

	// recentre
	const recenter = useViewerStore((s) => s.recenterRequest);
	useEffect(() => {
		const step = data?.steps[stepIndex];
		if (recenter > 0 && step) viewerRef.current?.frameStep(step, false);
	}, [recenter, data, stepIndex]);
}
